import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Snackbar } from '#src/atoms/snackbar/Snackbar/index.tsx';
import { LabelL } from '#src/atoms/typography/index.tsx';
import { SpentProgress } from '#src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/promo/SpentProgress/index.tsx';
import type { PaymentMethod as DepositMethod } from '#src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/types.tsx';
import { CreditCardPaymentMethod } from '#src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/types.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import type { Lifecycle } from '#src/hooks/useLifecycle.ts';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import type { MonetaryAmount } from '#src/lib/monetaryAmount.ts';

import { DepositContext } from './index.tsx';

interface Props {
  children: ReactNode;
}

const DepositActivePromotionalSpentNotification = () => {
  const { currentUser } = useCurrentUserContext();
  const { toMonetaryAmount } = useMonetaryAmount();

  const [previousActivePromotionalSpent, setPreviousActivePromotionalSpent] =
    useState<MonetaryAmount | undefined>();
  const [open, setOpen] = useState(false);

  if (
    !previousActivePromotionalSpent &&
    currentUser?.completablePromotionalEvent?.promotionalEventCompletion
  ) {
    setPreviousActivePromotionalSpent(
      toMonetaryAmount(
        currentUser.completablePromotionalEvent.promotionalEventCompletion
          .actualAmounts
      )
    );
  }

  if (
    previousActivePromotionalSpent &&
    currentUser?.completablePromotionalEvent?.promotionalEventCompletion &&
    !open &&
    !previousActivePromotionalSpent.isEqualTo(
      toMonetaryAmount(
        currentUser.completablePromotionalEvent.promotionalEventCompletion
          .actualAmounts
      )
    )
  ) {
    setPreviousActivePromotionalSpent(
      toMonetaryAmount(
        currentUser.completablePromotionalEvent.promotionalEventCompletion
          .actualAmounts
      )
    );
    setOpen(true);
  }

  if (!open) return null;

  return (
    <Snackbar
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      autoHideDuration={4000}
      message={
        <Vertical style={{ minWidth: '300px' }}>
          <LabelL bold>
            <FormattedMessage
              id="DepositActivePromotionalSpentNotification.winYourBox"
              defaultMessage="Win your reward box"
            />
          </LabelL>
          <SpentProgress />
        </Vertical>
      }
    />
  );
};

export const DepositProvider = ({ children }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const preferredDepositMethod = (
    currentUser?.userSettings.lifecycle as Lifecycle
  )?.preferredDepositMethod;
  const [defaultDepositMethod, setDefaultDepositMethod] = useState<
    DepositMethod | undefined
  >(
    preferredDepositMethod !== CreditCardPaymentMethod.CREDIT_CARD
      ? preferredDepositMethod
      : undefined
  );

  return (
    <DepositContext.Provider
      value={{ defaultDepositMethod, setDefaultDepositMethod }}
    >
      {children}
      <DepositActivePromotionalSpentNotification />
    </DepositContext.Provider>
  );
};
